import React, { ReactElement, useState } from "react"
import styled from "styled-components"
import { Icon } from "semantic-ui-react"
import {
  OrderType,
  OrderStatus,
  ManualOrderPacmanReason,
  ManualOrderLabel,
  ManualCtoBOrderPacmanAuthorizedStatus,
} from "types/order.types"
import { CarrierRole } from "types/carrier.types"
import { carrierTestData } from "utils/testParcelGenerators"

const TestingContainer = styled.div`
  background-color: #1e232c;
  padding: 10px;
  border-radius: 10px;
  margin-bottom: 20px;
`

const TestingTitle = styled.div`
  font-size: 14px;
  color: #fff;
`

const TestingSubtitle = styled.div`
  font-size: 9px;
  color: #fff;
  font-style: italic;
  margin-top: 10px;
  margin-bottom: 10px;
`

const TestingButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
`

const MiniButtonTestingContainer = styled.div`
  padding: 3px 6px;
  margin-right: 5px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 9px;
  position: relative;
  background-color: #333542;
  width: fit-content;
  align-self: flex-start;
  color: #fff;
  margin-bottom: 5px;

  &:hover {
    background-color: #3e404f;
  }

  &:first-of-type {
    margin-left: 3px;
  }
`

const MiniButtonTesting = ({ apiCallback, title }): ReactElement => {
  const [buttonClicked, setButtonClicked] = useState(false)

  React.useEffect(() => {
    if (buttonClicked === true) {
      setTimeout(() => setButtonClicked(false), 1000)
    }
  }, [buttonClicked])

  return (
    <MiniButtonTestingContainer
      onClick={() => {
        setButtonClicked(true)
        apiCallback()
      }}
    >
      {buttonClicked ? "Filled ✅" : title}
    </MiniButtonTestingContainer>
  )
}

interface CarrierTestingButtonsProps {
  orderType: OrderType
  onFillForm: (data: any, carrier: { value: string; label: string; type: CarrierRole }) => void
  companies: Array<{ value: string; label: string; type: CarrierRole }>
}

const CarrierTestingButtons = ({
  orderType,
  onFillForm,
  companies,
}: CarrierTestingButtonsProps): ReactElement | null => {
  const [actionsDisplayed, setActionsDisplayed] = useState(true)

  // Only show in non-production environments
  if (process.env.REACT_APP_STAGE === "production") {
    return null
  }

  const handleTestFill = (carrierKey: string) => {
    const testData = carrierTestData[carrierKey]
    if (!testData) return

    // Find the corresponding carrier in companies
    let carrier = companies.find(
      c => c.label.toLowerCase().includes(testData.name.toLowerCase()) || c.label.toLowerCase().includes(carrierKey),
    )

    // Fallback mappings for carriers that might have different labels
    if (!carrier) {
      const carrierMappings = {
        colissimo: companies.find(c => c.label.toLowerCase().includes("colissimo")),
        gls: companies.find(c => c.label.toLowerCase().includes("gls")),
        dhl: companies.find(c => c.label.toLowerCase().includes("dhl")),
        geodis: companies.find(c => c.label.toLowerCase().includes("geodis")),
        relaiscolis: companies.find(c => c.label.toLowerCase().includes("relais")),
        ups: companies.find(c => c.label.toLowerCase().includes("ups")),
      }
      carrier = carrierMappings[carrierKey]
    }

    if (!carrier) {
      console.warn(`No carrier found for ${carrierKey}`)
      return
    }

    // Generate parcel number
    const parcelNo = testData.generate()

    // Generate shipment number (parcel number + "-S")
    const shipmentNo = parcelNo + "-S"

    // Set status based on order type
    const parcel_status =
      orderType === OrderType.CtoB
        ? {
            value: ManualCtoBOrderPacmanAuthorizedStatus.DROPPED_TO_KEEPER,
            label: ManualOrderLabel.DROPPED_TO_KEEPER,
          }
        : {
            value: OrderStatus.IN_TRANSIT,
            label: ManualOrderLabel.IN_TRANSIT,
          }

    const formData = {
      ...testData.sampleData,
      parcelNo,
      shipmentNo,
      // Set status and reason based on order type
      parcel_status,
      reason: {
        value: ManualOrderPacmanReason.NO_ANNOUNCEMENT,
        label: ManualOrderLabel.NO_ANNOUNCEMENT,
      },
    }

    onFillForm(formData, carrier)
  }

  // Get available carriers based on order type
  const getAvailableCarriers = () => {
    const allCarriers = ["colissimo", "gls", "dhl", "geodis", "relaiscolis", "ups"]

    if (orderType === OrderType.CtoB) {
      // For CtoB, only Relais Colis and UPS are supported
      return allCarriers.filter(carrier => ["relaiscolis", "ups"].includes(carrier))
    }

    return allCarriers
  }

  const availableCarriers = getAvailableCarriers()

  return (
    <TestingContainer>
      <TestingTitle>
        🧪 Testing - Order Generator{" "}
        <Icon
          size={"small"}
          name={actionsDisplayed ? "eye" : "eye slash"}
          onClick={() => setActionsDisplayed(!actionsDisplayed)}
        />
      </TestingTitle>
      {actionsDisplayed && (
        <span>
          <TestingSubtitle>Auto-fill forms with valid carrier data ({orderType})</TestingSubtitle>
          <TestingButtonsContainer>
            {availableCarriers.map(carrierKey => (
              <MiniButtonTesting
                key={carrierKey}
                apiCallback={() => handleTestFill(carrierKey)}
                title={carrierTestData[carrierKey].name}
              />
            ))}
          </TestingButtonsContainer>
        </span>
      )}
    </TestingContainer>
  )
}

export default CarrierTestingButtons
